import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import { useTranslate } from 'react-admin';
import type { Block, BlockLessonContent, CompletionRule } from '../../../Courses/editors/BlockEditor/types';
import type { BlockInteractionResult } from '../../../../services/blockInteractionService';
import { BlockInteractionProvider } from './BlockInteractionContext';
import { BlockList } from './BlockPlayer';

const GRADEABLE = new Set(['inline_quiz', 'fill_blank', 'matching', 'ordering', 'simulation']);

function flatten(blocks: Block[]): Block[] {
    const out: Block[] = [];
    for (const b of blocks ?? []) {
        out.push(b);
        const d = b.data as { columns?: { blocks: Block[] }[]; items?: { blocks: Block[] }[]; tabs?: { blocks: Block[] }[] };
        for (const container of [...(d.columns ?? []), ...(d.items ?? []), ...(d.tabs ?? [])]) {
            out.push(...flatten(container.blocks ?? []));
        }
    }
    return out;
}

interface Props {
    lesson: { id: number; title: string; content?: BlockLessonContent | null; minScore?: number | null };
    /** Author preview: nothing is recorded, the complete button is hidden. */
    readOnly?: boolean;
    onComplete?: () => void;
    onProgressUpdate?: (lessonId: number, progressData: { status?: string; progressPercentage?: number; score?: string }) => Promise<boolean>;
}

/**
 * Learner view of a block lesson (plan §6.8): renders the blocks, tracks graded
 * attempts, mirrors the server-side completion rule as UI state and hands the
 * aggregated score to the course player before completing. The server remains
 * the authority (422 `completion_rule` is shown as a hint).
 */
export const BlockLessonPlayer: React.FC<Props> = ({ lesson, readOnly = false, onComplete, onProgressUpdate }) => {
    const translate = useTranslate();
    const content = lesson.content ?? { blocks: [] };
    const rule: CompletionRule = content.completionRule ?? 'view_only';
    const blocks = content.blocks ?? [];
    const gradeableIds = useMemo(() => flatten(blocks).filter((b) => GRADEABLE.has(b.type)).map((b) => b.id), [blocks]);
    const [latest, setLatest] = useState<Record<string, BlockInteractionResult>>({});
    const [completing, setCompleting] = useState(false);

    const onInteraction = useCallback((blockId: string, result: BlockInteractionResult) => {
        setLatest((prev) => ({ ...prev, [blockId]: result }));
    }, []);

    const attempted = gradeableIds.filter((id) => latest[id]);
    const score = gradeableIds.length > 0 ? Math.round((gradeableIds.reduce((sum, id) => sum + (latest[id]?.scorePercent ?? 0), 0) / gradeableIds.length)) : null;
    const minScore = lesson.minScore ?? 70;

    const missing = gradeableIds.length - attempted.length;
    const blockedReason =
        (rule === 'all_interactions' || rule === 'quiz_passed') && missing > 0
            ? translate('blockPlayer.completion.missing', { smart_count: missing })
            : rule === 'quiz_passed' && score !== null && score < minScore
                ? translate('blockPlayer.completion.belowMin', { score, min: minScore })
                : null;

    const complete = async () => {
        if (!onComplete) return;
        setCompleting(true);
        try {
            if (onProgressUpdate && score !== null) {
                await onProgressUpdate(lesson.id, { score: String(score), progressPercentage: 100 });
            }
            await onComplete();
        } finally {
            setCompleting(false);
        }
    };

    return (
        <Box className="oa-block-lesson-player">
            <BlockInteractionProvider lessonId={readOnly ? null : lesson.id} readOnly={readOnly} onInteraction={onInteraction}>
                <BlockList blocks={blocks} />
            </BlockInteractionProvider>

            {!readOnly && onComplete && (
                <Box className="oa-bp-complete" sx={{ mt: 4, pt: 2, borderTop: '1px solid var(--oa-divider)' }}>
                    {gradeableIds.length > 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {translate('blockPlayer.completion.progress', { done: attempted.length, total: gradeableIds.length })}
                            {score !== null && attempted.length > 0 ? ` · ${translate('blockPlayer.completion.score', { score })}` : ''}
                        </Typography>
                    )}
                    {blockedReason && <Alert severity="info" sx={{ mb: 1.5 }}>{blockedReason}</Alert>}
                    <Button variant="contained" startIcon={completing ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutline />} disabled={!!blockedReason || completing} onClick={complete}>
                        {translate('blockPlayer.completion.button')}
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default BlockLessonPlayer;
